import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import aviso, { textoDoErro } from '../lib/aviso';
import {
    LuPlus, LuSearch, LuChevronLeft, LuChevronRight, LuPencil, LuEye, LuTrash2, LuShieldCheck, LuSettings2, LuHistory,
    LuInfo,
} from 'react-icons/lu';
import { apiFetch } from '../services/api';
import { relerUsuario } from '../services/usuarioAtual';
import { CODIGO_ADMINISTRADOR, ehAdministradorDaEmpresa, temPermissao } from '../config/permissions';
import PageHeader from '../components/PageHeader';
import RecordPanel, { PanelSection } from '../components/RecordPanel';
import SecaoHistorico from '../components/historico/SecaoHistorico';
import { ListRow, PerPage, RowAction, RowActions, ListCard, ListCards } from '../components/ListKit';
import Select from '../components/Select';
import '../components/ListKit.css';
import { useCelular } from '../hooks/useCelular';
import { useRecorte } from '../hooks/useRecorte';
// Global disfarçado — ver o comentário equivalente em ContactsPage.tsx.
import './TipoUsuarioPage.css';
import TipoUsuarioService, {
    type TipoUsuario, type ImpactoExclusao,
} from '../services/TipoUsuarioService';
import PermissaoAcessoService, { type Permissao, type GrupoPermissao } from '../services/PermissaoAcessoService';
import { PERMISSION_LABELS, getPermissionIconLu } from '../utils/permissionLabels';

const apiUrl = import.meta.env.VITE_API_URL;

/** Ordem fixa dos grupos do catálogo (CatalogoPermissoes na API). */
const ORDEM_GRUPOS: GrupoPermissao[] = ['Operação', 'Gestão', 'Marketing', 'Configuração', 'Sistema'];

/** Rótulo curto da derivada, ao lado do nome: o que ela acrescenta à base. */
const ROTULO_TIPO: Record<number, string> = { 2: 'Gerenciar', 3: 'Ver de todos' };

const ehDerivada = (p: Permissao) => (p.chavesBase?.length ?? 0) > 0;

/** Id do perfil do usuário logado (`role` do Auth/Me). */
const meuPerfilId = (): number | null => {
    try {
        const id = Number(JSON.parse(localStorage.getItem('user') || '{}')?.role);
        return Number.isFinite(id) && id > 0 ? id : null;
    } catch {
        return null;
    }
};
const POR_PAGINA = [25, 50, 100];

type Modo = 'ver' | 'editar';

interface Rascunho { id: number | null; nome: string; canReceiveLead: boolean }
const VAZIO: Rascunho = { id: null, nome: '', canReceiveLead: false };

const iniciais = (nome: string) => (nome.trim()[0] ?? '?').toUpperCase();

const TipoUsuarioPage = () => {
    const celular = useCelular();
    /* Quem não é admin (tem `gestao_tipos_usuario`) esbarra em três regras da
       API: não altera o próprio perfil, não mexe no perfil Administrador e só
       concede/remove chave que ele mesmo tem. A tela evita as três. */
    const souAdmin = ehAdministradorDaEmpresa();
    const meuPerfil = meuPerfilId();
    const [tipos, setTipos] = useState<TipoUsuario[]>([]);
    const [usuarios, setUsuarios] = useState<{ id: number; nome: string; email: string; tipoUsuarioId: number }[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    /* Recorte de trabalho, na sessao da aba. (A `aba` mais abaixo e do painel
       do perfil, nao da lista.) */
    const [recorte, gravarRecorte] = useRecorte('tu:recorte', {
        busca: '', pagina: 1, porPagina: 25,
    });
    const [busca, setBusca] = useState(recorte.busca);
    const [pagina, setPagina] = useState(recorte.pagina);
    const [porPagina, setPorPagina] = useState(recorte.porPagina);
    useEffect(() => {
        gravarRecorte({ busca, pagina, porPagina });
    }, [busca, pagina, porPagina, gravarRecorte]);

    const [aberto, setAberto] = useState<TipoUsuario | null>(null);
    const [modo, setModo] = useState<Modo>('ver');
    const [rascunho, setRascunho] = useState<Rascunho>(VAZIO);
    const [salvando, setSalvando] = useState(false);
    const [erros, setErros] = useState<Record<string, string>>({});

    /* Permissões: só do perfil aberto — o catálogo é global, mas o vínculo
       exige uma chamada por perfil. Elas são salvas junto com o perfil, no
       mesmo DTO, então vivem no modo edição e não se aplicam sozinhas. */
    const [aba, setAba] = useState<'gerais' | 'permissoes' | 'historico'>('gerais');
    const [catalogo, setCatalogo] = useState<Permissao[]>([]);
    const [selecionadas, setSelecionadas] = useState<number[]>([]);
    const [carregandoPerm, setCarregandoPerm] = useState(false);

    const [impacto, setImpacto] = useState<ImpactoExclusao | null>(null);

    /* O Administrador sempre tem acesso a tudo e as permissões dele não se editam
       (10/10/2026): a API calcula "tudo" no login e recusa a edição. Mostrar a
       lista não diria nada, então a aba some. Na empresa interna (1) a lista
       continua, porque é por ela que vêm as permissões de sistema. */
    const empresaInterna = useMemo(() => {
        try { return Number(JSON.parse(localStorage.getItem('user') || '{}')?.empresaId) === 1; }
        catch { return false; }
    }, []);
    const adminFixo = aberto?.codigo === CODIGO_ADMINISTRADOR && !empresaInterna;
    const [checandoImpacto, setChecandoImpacto] = useState(false);

    /* ─── Dados ──────────────────────────────────────────────── */

    const carregar = useCallback(async () => {
        setIsLoading(true);
        try {
            setTipos(await TipoUsuarioService.buscarTodos());
        } catch (e: any) {
            aviso.erro(textoDoErro(e, 'Não foi possível carregar os perfis.'));
        } finally {
            setIsLoading(false);
        }
    }, []);

    // Uma chamada a mais só para responder "este perfil ainda é usado?".
    const carregarUsuarios = useCallback(async () => {
        try {
            const r = await apiFetch(`${apiUrl}/api/Usuario/BuscarTodos`);
            const d = await r.json();
            if (d.sucesso) setUsuarios((d.dados || []).map((u: any) => ({
                id: u.userId, nome: u.userName, email: u.userEmail,
                tipoUsuarioId: u.userTypeuserid,
            })));
        } catch (e) { console.error(e); }
    }, []);

    useEffect(() => { carregar(); carregarUsuarios(); }, [carregar, carregarUsuarios]);
    // Mudar um filtro volta para a pagina 1 - mas nao na montagem, senao a
    // pagina restaurada da sessao se perde.
    const primeiraRodada = useRef(true);
    useEffect(() => {
        if (primeiraRodada.current) { primeiraRodada.current = false; return; }
        setPagina(1);
    }, [busca, porPagina]);

    const idAberto = aberto?.id;
    useEffect(() => {
        if (!aberto) { setCatalogo([]); setSelecionadas([]); return; }
        let vivo = true;
        setCarregandoPerm(true);
        Promise.all([
            PermissaoAcessoService.buscarCatalogo(),
            idAberto ? PermissaoAcessoService.buscarPermissoesPorTipoUsuario(idAberto) : Promise.resolve([]),
        ]).then(([cat, minhas]) => {
            if (!vivo) return;
            setCatalogo(cat);
            setSelecionadas(minhas);
        }).catch(() => {
            if (vivo) { setCatalogo([]); setSelecionadas([]); }
        }).finally(() => { if (vivo) setCarregandoPerm(false); });
        return () => { vivo = false; };
    }, [aberto, idAberto]);

    /** Catálogo agrupado por `grupo` (Operação, Gestão…), na ordem do catálogo.
     *  Dentro do grupo, cada derivada (Gerenciar/Ver de todos) vem logo abaixo
     *  da primeira base dela, recuada. */
    const porGrupo = useMemo(() => {
        const m = new Map<string, Permissao[]>();
        catalogo.forEach(p => {
            const k = p.grupo ?? 'Outras';
            m.set(k, [...(m.get(k) ?? []), p]);
        });
        const posicao = (g: string) => {
            const i = ORDEM_GRUPOS.indexOf(g as GrupoPermissao);
            return i === -1 ? ORDEM_GRUPOS.length : i;
        };
        return [...m.entries()]
            .sort((a, b) => posicao(a[0]) - posicao(b[0]))
            .map(([grupo, itens]) => {
                const bases = itens.filter(p => !ehDerivada(p));
                const derivadas = itens.filter(ehDerivada);
                const chavesDoGrupo = new Set(bases.map(b => b.nomePermissao));
                const ordenados: Permissao[] = [];
                bases.forEach(b => {
                    ordenados.push(b);
                    derivadas
                        .filter(d => d.chavesBase.find(c => chavesDoGrupo.has(c)) === b.nomePermissao)
                        .forEach(d => ordenados.push(d));
                });
                // Derivada cuja base mora em outro grupo: vai para o fim do grupo dela.
                derivadas.filter(d => !ordenados.includes(d)).forEach(d => ordenados.push(d));
                return [grupo, ordenados] as const;
            });
    }, [catalogo]);

    /* O `nomeTela` da API já está escrito para o usuário final ("Importar e
       excluir contatos"); o mapa local é só reserva. */
    const rotulo = (p: Permissao) =>
        p.nomeTela?.trim() || PERMISSION_LABELS[p.nomePermissao] || p.nomePermissao;

    /** Quem não é admin só concede/remove chave que ele mesmo tem. */
    const podeAlternar = (p: Permissao) => souAdmin || temPermissao(p.nomePermissao);

    const porChave = useMemo(() => new Map(catalogo.map(p => [p.nomePermissao, p])), [catalogo]);
    const porId = useMemo(() => new Map(catalogo.map(p => [p.idPermissao, p])), [catalogo]);

    /** Tira as derivadas que ficaram sem nenhuma base marcada. */
    const semDerivadasOrfas = useCallback((ids: number[]) => {
        const chaves = new Set(ids.map(id => porId.get(id)?.nomePermissao));
        return ids.filter(id => {
            const p = porId.get(id);
            return !p || !ehDerivada(p) || p.chavesBase.some(b => chaves.has(b));
        });
    }, [porId]);

    /* Base ↔ derivada, a mesma regra que a API valida ("A permissão X exige Y"):
       marcar a derivada sem base marca a primeira base; desmarcar a base
       desmarca as derivadas que ficaram sem nenhuma. */
    const alternar = (id: number) => setSelecionadas(prev => {
        const p = porId.get(id);
        if (!p) return prev;
        const marcando = !prev.includes(id);
        let novo = marcando ? [...prev, id] : prev.filter(x => x !== id);

        if (marcando && ehDerivada(p)) {
            const temBase = p.chavesBase.some(b => novo.some(x => porId.get(x)?.nomePermissao === b));
            const base = porChave.get(p.chavesBase[0]);
            if (!temBase && base) novo = [...novo, base.idPermissao];
        }

        return marcando ? novo : semDerivadasOrfas(novo);
    });

    /* Marcar/desmarcar todas respeita o que a pessoa pode alternar: as chaves
       que ela não tem ficam como estão. */
    const marcarTodas = () => setSelecionadas(prev => [
        ...new Set([...prev, ...catalogo.filter(podeAlternar).map(p => p.idPermissao)]),
    ]);
    const desmarcarTodas = () => setSelecionadas(prev => semDerivadasOrfas(
        prev.filter(id => {
            const p = porId.get(id);
            return p ? !podeAlternar(p) : false;
        })));

    /* ─── Derivados ──────────────────────────────────────────── */

    const usuariosDe = useCallback(
        (tipoId: number) => usuarios.filter(u => u.tipoUsuarioId === tipoId), [usuarios]);

    const filtrados = useMemo(() => {
        const t = busca.trim().toLowerCase();
        return t ? tipos.filter(x => x.nome.toLowerCase().includes(t)) : tipos;
    }, [tipos, busca]);

    const totalPaginas = Math.max(1, Math.ceil(filtrados.length / porPagina));
    const paginaAtual = Math.min(pagina, totalPaginas);
    const inicio = (paginaAtual - 1) * porPagina;
    const visiveis = filtrados.slice(inicio, inicio + porPagina);

    /* ─── Painel ─────────────────────────────────────────────── */

    const abrirVer = (t: TipoUsuario) => { setAberto(t); setModo('ver'); setAba('gerais'); setErros({}); setImpacto(null); };
    const abrirEdicao = (t: TipoUsuario) => {
        setAberto(t);
        setRascunho({ id: t.id, nome: t.nome, canReceiveLead: t.canReceiveLead });
        setModo('editar'); setAba('gerais'); setErros({});
    };
    const abrirNovo = () => {
        setAberto({ id: 0, nome: '', codigo: null, canReceiveLead: false });
        setRascunho(VAZIO); setModo('editar'); setAba('gerais'); setErros({});
    };
    const fechar = () => { setAberto(null); setErros({}); setImpacto(null); };

    const ehNovo = modo === 'editar' && rascunho.id === null;

    /** Perfil Administrador: tem tudo, inclusive o que for criado depois. */
    const abertoEhAdmin = aberto?.codigo === CODIGO_ADMINISTRADOR;
    /* Não-admin: o próprio perfil e o do Administrador abrem só em leitura. */
    const soLeitura = !souAdmin && !!aberto && aberto.id !== 0
        && (abertoEhAdmin || aberto.id === meuPerfil);
    const motivoSoLeitura = !soLeitura ? null
        : abertoEhAdmin
            ? 'Só o administrador altera o perfil Administrador.'
            : 'Você não pode alterar o seu próprio perfil.';

    const salvar = async () => {
        if (!rascunho.nome.trim()) { setErros({ nome: 'Informe o nome do perfil.' }); return; }
        setSalvando(true);
        try {
            // As permissões viajam no MESMO DTO do perfil: um Salvar só.
            const dto = {
                nome: rascunho.nome.trim(),
                canReceiveLead: rascunho.canReceiveLead,
                permissoesIds: selecionadas,
            };
            if (rascunho.id) await TipoUsuarioService.atualizar(rascunho.id, dto);
            else await TipoUsuarioService.criar(dto);
            aviso.ok(rascunho.id
                ? 'Perfil atualizado. As permissões valem na próxima renovação da sessão de cada usuário (até 1 hora).'
                : 'Perfil criado.');
            /* O próprio perfil (só o admin chega aqui): renova o token para o
               claim de permissões vir novo e relê o Auth/Me — o menu reflete
               na hora, sem esperar a renovação natural. */
            if (rascunho.id && rascunho.id === meuPerfil) await renovarMinhaSessao();
            await carregar();
            fechar();
        } catch (e: any) {
            aviso.erro(textoDoErro(e, 'Não foi possível salvar o perfil.'));
        } finally {
            setSalvando(false);
        }
    };

    const renovarMinhaSessao = async () => {
        try {
            const r = await apiFetch(`${apiUrl}/api/Auth/refresh-token`, { method: 'POST' });
            if (r.ok) await relerUsuario(true);
        } catch {
            // Sem renovar agora, vale a renovação natural (até 1 hora).
        }
    };

    /* Exclusão passa pelo ImpactoExclusao ANTES de confirmar. É um
       comportamento que já existia e é raro: mostra quem seria afetado em vez
       de um "tem certeza?" cego. */
    /* O id do que vai sair anda em estado próprio porque a exclusão tem DUAS
       portas e um passo intermediário: a lixeira da linha e o rodapé do painel
       levam ao mesmo painel de impacto, e quem confirma lá precisa saber de
       quem se trata. Antes isso vinha de `rascunho.id`, que só existe com o
       painel em edição — pela lixeira ele estaria vazio, ou pior, apontando
       para outro perfil. */
    const [idParaExcluir, setIdParaExcluir] = useState<number | null>(null);

    const pedirExclusao = async (alvo?: TipoUsuario) => {
        const id = alvo?.id ?? rascunho.id;
        if (!id) return;
        setIdParaExcluir(id);
        setChecandoImpacto(true);
        try {
            setImpacto(await TipoUsuarioService.impactoExclusao(id));
        } catch (e: any) {
            aviso.erro(textoDoErro(e, 'Não foi possível verificar o impacto da exclusão.'));
        } finally {
            setChecandoImpacto(false);
        }
    };

    const confirmarExclusao = async () => {
        if (!impacto?.podeExcluir || !idParaExcluir) return;
        setSalvando(true);
        try {
            await TipoUsuarioService.deletar(idParaExcluir);
            aviso.ok('Perfil excluído.');
            await carregar();
            fechar();
        } catch (e: any) {
            aviso.erro(textoDoErro(e, 'Não foi possível excluir o perfil.'));
        } finally {
            setSalvando(false);
        }
    };

    /* ─── Render ─────────────────────────────────────────────── */

    /* O Editar mora DENTRO da aba, não acima dela (2026-09-12): um botão sobre
       a barra de abas promete editar o painel inteiro, e aqui cada aba tem
       assunto próprio. As duas abas compartilham o botão porque as permissões
       viajam no mesmo DTO do perfil — um Salvar só. */
    const botaoEditar = aberto && modo === 'ver' && (soLeitura ? (
        <p className="tu-aviso"><LuInfo size={13} aria-hidden="true" /> {motivoSoLeitura}</p>
    ) : (
        <div className="rp-actions is-inline">
            <button type="button" className="rp-btn is-primary" onClick={() => abrirEdicao(aberto)}>
                <LuPencil size={13} /> Editar
            </button>
        </div>
    ));

    const COLUNAS ='1.6fr 1fr .8fr 78px';

    /* O aviso de "nada aqui" e o rodapé de paginação servem à tabela (no
       computador) e aos cartões (no celular) — por isso saem do meio do JSX. */
    const listaVazia = (
        <div className="lk-blank">
            <div className="lk-blank-title">
                {tipos.length === 0 ? 'Nenhum perfil cadastrado' : 'Nada com esse nome'}
            </div>
            <p className="lk-blank-text">
                {tipos.length === 0
                    ? 'Perfis definem o que cada usuário enxerga e se ele entra no rodízio de leads.'
                    : 'Ajuste a busca para ver os outros.'}
            </p>
            {tipos.length === 0 && (
                <button type="button" className="lk-btn is-primary" onClick={abrirNovo}>
                    <LuPlus size={14} /> Novo perfil
                </button>
            )}
        </div>
    );

    const rodape = (
        <div className="lk-foot">
            <PerPage value={porPagina} options={POR_PAGINA} onChange={setPorPagina} />
            <span className="lk-range">{inicio + 1}–{Math.min(inicio + porPagina, filtrados.length)} de {filtrados.length}</span>
            <span className="lk-pages">
                <button type="button" className="lk-page" aria-label="Página anterior"
                    disabled={paginaAtual === 1} onClick={() => setPagina(p => Math.max(1, p - 1))}>
                    <LuChevronLeft size={13} /></button>
                <span className="lk-page is-current">{paginaAtual}</span>
                <span className="lk-muted">de {totalPaginas}</span>
                <button type="button" className="lk-page" aria-label="Próxima página"
                    disabled={paginaAtual === totalPaginas} onClick={() => setPagina(p => Math.min(totalPaginas, p + 1))}>
                    <LuChevronRight size={13} /></button>
            </span>
        </div>
    );

    return (
        <div className="page-shell lk-screen">
            <PageHeader
                title="Perfis"
                subtitle="O que cada tipo de usuário pode fazer"
                /* Lista vazia: a porta é a do aviso, no meio da tela. */
                actions={!isLoading && tipos.length === 0 ? undefined :
                    <div className="lk-actions">
                        <button type="button" className="lk-btn is-primary" onClick={abrirNovo}>
                            <LuPlus size={14} /> Novo perfil
                        </button>
                    </div>
                }
            />

            <div className="page-body lk-fill">
                <div className="lk-filters">
                    <label className="lk-search">
                        <LuSearch size={14} />
                        <input type="search" placeholder="Buscar por nome…"
                            value={busca} onChange={e => setBusca(e.target.value)} />
                    </label>
                    <span className="lk-count">
                        {tipos.length} {tipos.length === 1 ? 'perfil' : 'perfis'}
                        {filtrados.length !== tipos.length && ` · ${filtrados.length} no filtro`}
                    </span>
                </div>

                {/* Celular: a grade de quatro colunas não cabe na largura do
                    telefone, então a linha vira cartão — o nome, quanta gente
                    usa o perfil e se ele entra no rodízio. As permissões só
                    existem para o perfil aberto, então ficam no painel. */}
                {celular ? (
                    <>
                        <ListCards>
                            {isLoading
                                ? Array.from({ length: 6 }).map((_, i) => <div key={i} className="lk-skeleton" />)
                                : visiveis.map(t => {
                                    const n = usuariosDe(t.id).length;
                                    return (
                                        <ListCard
                                            key={t.id}
                                            avatar={<span className="lk-ava" aria-hidden="true">{iniciais(t.nome)}</span>}
                                            titulo={t.nome}
                                            apoio={n === 0 ? 'nenhum usuário' : `${n} ${n === 1 ? 'usuário' : 'usuários'}`}
                                            estado={
                                                <span className={`rp-badge ${t.canReceiveLead ? 'is-good' : 'is-lost'}`}>
                                                    {t.canReceiveLead ? 'Recebe lead' : 'Não recebe lead'}
                                                </span>
                                            }
                                            selected={aberto?.id === t.id}
                                            label={`Abrir ${t.nome}`}
                                            onOpen={() => abrirVer(t)}
                                            acoes={
                                                <RowAction icon={<LuTrash2 size={15} />} label="Excluir perfil" danger
                                                    onClick={() => pedirExclusao(t)} />
                                            }
                                        />
                                    );
                                })}
                        </ListCards>
                        {!isLoading && filtrados.length === 0 && listaVazia}
                        {!isLoading && filtrados.length > 0 && rodape}
                    </>
                ) : (
                <div className="lk-table">
                    <div className="lk-scroll">
                        <div className="lk-row" style={{ gridTemplateColumns: COLUNAS }}>
                            <div className="lk-head">Perfil</div>
                            <div className="lk-head">Recebe lead</div>
                            <div className="lk-head">Usuários</div>
                            <div className="lk-head lk-head-acts">Ações</div>
                        </div>
                        <div className="lk-body">
                            {isLoading
                                ? Array.from({ length: 6 }).map((_, i) => <div key={i} className="lk-skeleton" />)
                                : visiveis.map(t => {
                                    const n = usuariosDe(t.id).length;
                                    return (
                                        <ListRow key={t.id} columns={COLUNAS}
                                            selected={aberto?.id === t.id}>
                                            <span className="lk-cell lk-strong"><span>{t.nome}</span></span>
                                            <span className="lk-cell">
                                                <span className={`rp-badge ${t.canReceiveLead ? 'is-good' : 'is-lost'}`}>
                                                    {t.canReceiveLead ? 'Sim' : 'Não'}
                                                </span>
                                            </span>
                                            <span className={`lk-cell ${n ? 'lk-muted' : 'lk-empty-value'}`}>
                                                <span>{n || 'nenhum'}</span>
                                            </span>
                                            <RowActions>
                                                <RowAction icon={<LuEye size={14} />} label="Ver perfil"
                                                    onClick={() => abrirVer(t)} />
                                                <RowAction icon={<LuTrash2 size={14} />} label="Excluir perfil" danger
                                                    onClick={() => pedirExclusao(t)} />
                                            </RowActions>
                                        </ListRow>
                                    );
                                })}
                        </div>
                    </div>

                    {!isLoading && filtrados.length === 0 && listaVazia}
                    {!isLoading && filtrados.length > 0 && rodape}
                </div>
                )}
            </div>

            <RecordPanel
                open={!!aberto}
                onClose={fechar}
                initials={iniciais(modo === 'editar' ? (rascunho.nome || 'N') : (aberto?.nome ?? ''))}
                title={modo === 'editar' ? (rascunho.nome.trim() || (ehNovo ? 'Novo perfil' : 'Perfil')) : (aberto?.nome ?? '')}
                subtitle="Perfil de acesso"
                editing={modo === 'editar'}
                wide
                tabs={[
                    { id: 'gerais', label: 'Dados gerais', icon: <LuSettings2 size={13} /> },
                    ...(adminFixo ? [] : [{
                        id: 'permissoes', label: 'Permissões', icon: <LuShieldCheck size={13} />,
                        badge: catalogo.length ? `${selecionadas.length}/${catalogo.length}` : undefined,
                    }]),
                    /* Histórico (fase 4): só de perfil que já existe. Painel com abas ganha uma aba. */
                    ...(aberto && !ehNovo && modo === 'ver'
                        ? [{ id: 'historico', label: 'Histórico', icon: <LuHistory size={13} /> }]
                        : []),
                ]}
                activeTab={aba}
                onTabChange={id => setAba(id as 'gerais' | 'permissoes' | 'historico')}
                onBack={ehNovo ? undefined : () => setModo('ver')}
                footer={
                    <>
                        {!ehNovo && (
                            <button type="button" className="rp-btn is-danger" onClick={() => pedirExclusao()}
                                disabled={salvando || checandoImpacto}>
                                {checandoImpacto ? 'Verificando…' : 'Excluir perfil'}
                            </button>
                        )}
                        <button type="button" className="rp-btn"
                            onClick={() => (ehNovo ? fechar() : setModo('ver'))} disabled={salvando}>Cancelar</button>
                        {!soLeitura && (
                            <button type="button" className="rp-btn is-primary" onClick={salvar} disabled={salvando}>
                                {salvando ? 'Salvando…' : 'Salvar'}
                            </button>
                        )}
                    </>
                }
            >
                {/* ─── Aba: dados gerais ─── */}

                {aba === 'gerais' && modo === 'ver' && aberto && (
                    <>
                        {botaoEditar}
                        <PanelSection title="Configuração">
                            <div className="rp-kv">
                                <span className="rp-k">Recebe lead</span>
                                <span className="rp-v">
                                    <span className={`rp-badge ${aberto.canReceiveLead ? 'is-good' : 'is-lost'}`}>
                                        {aberto.canReceiveLead ? 'Sim' : 'Não'}
                                    </span> no rodízio
                                </span>
                                <span className="rp-k">Permissões</span>
                                <span className="rp-v">
                                    {adminFixo
                                        ? 'Acesso total. O Administrador sempre vê tudo.'
                                        : carregandoPerm
                                            ? <i className="rp-v is-empty">carregando…</i>
                                            : `${selecionadas.length} de ${catalogo.length}`}
                                </span>
                            </div>
                        </PanelSection>

                        <PanelSection title={`Usuários com este perfil · ${usuariosDe(aberto.id).length}`}>
                            {usuariosDe(aberto.id).length === 0
                                ? <p className="rp-none">Nenhum usuário usa este perfil.</p>
                                : usuariosDe(aberto.id).slice(0, 8).map(u => (
                                    <div className="rp-link" key={u.id}>
                                        <div>
                                            <div className="rp-link-title">{u.nome}</div>
                                            <div className="rp-link-meta">{u.email}</div>
                                        </div>
                                    </div>
                                ))}
                        </PanelSection>
                    </>
                )}

                {aba === 'gerais' && modo === 'editar' && (
                    <PanelSection title="Configuração">
                        <div className="rp-field">
                            <label className="rp-label" htmlFor="tp-nome">Nome do perfil</label>
                            <input id="tp-nome" className={`rp-input ${erros.nome ? 'has-error' : ''}`} autoFocus
                                value={rascunho.nome} onChange={e => setRascunho(r => ({ ...r, nome: e.target.value }))} />
                            {erros.nome && <p className="rp-error">{erros.nome}</p>}
                        </div>
                        <div className="rp-field">
                            <label className="rp-label" htmlFor="tp-lead">Recebe lead no rodízio</label>
                            <Select id="tp-lead" value={rascunho.canReceiveLead ? 'sim' : 'nao'}
                                options={[{ value: 'nao', label: 'Não' }, { value: 'sim', label: 'Sim' }]}
                                onChange={v => setRascunho(r => ({ ...r, canReceiveLead: v === 'sim' }))} />
                            <p className="rp-toggle-hint" style={{ marginTop: 5 }}>
                                Usuários deste perfil recebem leads automaticamente na distribuição.
                            </p>
                        </div>
                    </PanelSection>
                )}

                {/* ─── Aba: permissões ───
                    No modo ver é leitura; no modo editar são interruptores, e
                    salvam junto com o perfil, no mesmo Salvar. */}

                {aba === 'permissoes' && !adminFixo && (
                    <>
                    {botaoEditar}
                    <PanelSection title={`Acesso por tela · ${selecionadas.length} de ${catalogo.length}`}>
                        {modo === 'editar' && catalogo.length > 0 && (
                            <div className="rp-sec-bar">
                                <button type="button" className="rp-link-btn"
                                    disabled={catalogo.filter(podeAlternar).every(p => selecionadas.includes(p.idPermissao))}
                                    onClick={marcarTodas}>
                                    Marcar todas
                                </button>
                                <button type="button" className="rp-link-btn"
                                    disabled={!selecionadas.some(id => { const p = porId.get(id); return !!p && podeAlternar(p); })}
                                    onClick={desmarcarTodas}>
                                    Desmarcar todas
                                </button>
                            </div>
                        )}

                        {carregandoPerm ? (
                            <p className="rp-none">Carregando permissões…</p>
                        ) : catalogo.length === 0 ? (
                            <p className="rp-none">Nenhuma permissão no catálogo.</p>
                        ) : porGrupo.map(([grupo, itens]) => {
                            const visiveis = modo === 'ver'
                                ? itens.filter(p => selecionadas.includes(p.idPermissao))
                                : itens;
                            if (visiveis.length === 0) return null;
                            return (
                                <div key={grupo}>
                                    <div className="rp-group">{grupo}</div>
                                    {visiveis.map(p => {
                                        const bloqueada = !podeAlternar(p);
                                        return (
                                            <div className={`rp-toggle-row ${ehDerivada(p) ? 'tu-derivada' : ''}`}
                                                key={p.idPermissao}
                                                title={modo === 'editar' && bloqueada ? 'Você não tem esta permissão' : undefined}>
                                                {!ehDerivada(p) && (
                                                    <span className="rp-toggle-ico">{getPermissionIconLu(p.nomePermissao)}</span>
                                                )}
                                                <span className="rp-toggle-text">
                                                    <span className="rp-toggle-label">
                                                        {rotulo(p)}
                                                        {p.tipo && ROTULO_TIPO[p.tipo] && (
                                                            <span className="tu-tipo">{ROTULO_TIPO[p.tipo]}</span>
                                                        )}
                                                    </span>
                                                </span>
                                                {modo === 'editar' ? (
                                                    <label className="rp-switch">
                                                        <input
                                                            type="checkbox"
                                                            checked={selecionadas.includes(p.idPermissao)}
                                                            disabled={bloqueada}
                                                            onChange={() => alternar(p.idPermissao)}
                                                            aria-label={rotulo(p)}
                                                        />
                                                        <span className="rp-switch-track" />
                                                        <span className="rp-switch-thumb" />
                                                    </label>
                                                ) : (
                                                    <span className="rp-badge is-good">ativa</span>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            );
                        })}

                        {modo === 'ver' && !carregandoPerm && selecionadas.length === 0 && catalogo.length > 0 && (
                            <p className="rp-none">Este perfil não tem nenhuma permissão ativa.</p>
                        )}
                    </PanelSection>
                    </>
                )}

                {aba === 'historico' && modo === 'ver' && aberto && (
                    <SecaoHistorico entidade="perfil" id={aberto.id} />
                )}
            </RecordPanel>

            {/* O impacto vira um segundo painel só porque é um desfecho: ou some
                com um "Entendi", ou o perfil deixa de existir. Não é um modo. */}
            <RecordPanel
                open={impacto !== null}
                onClose={() => setImpacto(null)}
                initials="!"
                title={impacto?.podeExcluir
                    ? `Excluir "${impacto?.tipoUsuarioNome}"?`
                    : `Não dá para excluir "${impacto?.tipoUsuarioNome}"`}
                subtitle={impacto?.motivoBloqueio
                    || (impacto?.podeExcluir ? 'Nada depende deste perfil' : 'Há coisas dependendo dele')}
                editing
                footer={
                    <>
                        <button type="button" className="rp-btn" onClick={() => setImpacto(null)}>
                            {impacto?.podeExcluir ? 'Cancelar' : 'Entendi'}
                        </button>
                        {impacto?.podeExcluir && (
                            <button type="button" className="rp-btn is-danger" onClick={confirmarExclusao} disabled={salvando}>
                                {salvando ? 'Excluindo…' : 'Excluir mesmo assim'}
                            </button>
                        )}
                    </>
                }
            >
                <PanelSection title={`Usuários afetados · ${impacto?.usuarios.length ?? 0}`}>
                    {(impacto?.usuarios.length ?? 0) === 0
                        ? <p className="rp-none">Nenhum usuário usa este perfil.</p>
                        : impacto!.usuarios.slice(0, 8).map(u => (
                            <div className="rp-link" key={u.userId}>
                                <div>
                                    <div className="rp-link-title">{u.userName}</div>
                                    <div className="rp-link-meta">{u.userEmail}</div>
                                </div>
                            </div>
                        ))}
                </PanelSection>

                <PanelSection title={`Permissões que somem · ${impacto?.permissoes.length ?? 0}`}>
                    {(impacto?.permissoes.length ?? 0) === 0
                        ? <p className="rp-none">Nenhuma permissão ligada a este perfil.</p>
                        : impacto!.permissoes.slice(0, 8).map(p => (
                            <div className="rp-link" key={p.idPermissao}>
                                <div>
                                    <div className="rp-link-title">{p.nomePermissao}</div>
                                    <div className="rp-link-meta">{p.nomeTela}</div>
                                </div>
                            </div>
                        ))}
                </PanelSection>
            </RecordPanel>

        </div>
    );
};

export default TipoUsuarioPage;
